/**
 * Sets document title, meta description, canonical URL, Open Graph / Twitter tags,
 * and optional VideoObject JSON-LD for a route. Restores previous values on unmount.
 *
 * Does not change the `robots` meta unless `noindex` or `forceIndex` is set, so
 * staging `noindex` from `public/index.html` is preserved on marketing routes.
 * Likewise, canonical `<link>` is only updated when the shell already includes one
 * (production); staging intentionally omits it and must not get a production URL.
 *
 * Use `useNoIndex()` for private app routes that should not appear in search results.
 */
import { useEffect } from "react";
import {
  PLATFORM_DEFAULT_TITLE,
  PLATFORM_META_DESCRIPTION,
  PLATFORM_OG_IMAGE,
  PLATFORM_SITE_URL,
} from "../constants/platformCopy";

const DEFAULT_CANONICAL = `${PLATFORM_SITE_URL}/`;

type VideoMeta = {
  name: string;
  description: string;
  contentUrl: string;
  uploadDate: string;
  duration: string;
  thumbnailUrl?: string;
};

export type PageMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  /** When true, sets `noindex, nofollow`. */
  noindex?: boolean;
  /**
   * When true, sets `index, follow` even if the shell already has noindex (e.g. staging).
   * By default, an existing noindex from `index.html` is preserved.
   */
  forceIndex?: boolean;
  ogImage?: string;
  video?: VideoMeta;
};

function setMetaContent(
  attribute: "name" | "property",
  key: string,
  content: string
): HTMLMetaElement {
  let element = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
  return element;
}

function upsertJsonLdScript(id: string, data: Record<string, unknown>): HTMLScriptElement {
  let script = document.getElementById(id) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
  return script;
}

/**
 * Applies `noindex, nofollow` to the current page without changing title or canonical.
 * Use on authenticated routes (dashboard, private canvas) so they do not compete
 * with marketing pages in search results.
 */
export function useNoIndex(): void {
  useEffect(() => {
    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousRobots =
      robotsMeta?.getAttribute("content") ?? "index, follow, max-image-preview:large";

    setMetaContent("name", "robots", "noindex, nofollow");

    return () => {
      if (robotsMeta) {
        robotsMeta.setAttribute("content", previousRobots);
      }
    };
  }, []);
}

export function usePageMeta({
  title,
  description,
  canonicalPath,
  noindex = false,
  forceIndex = false,
  ogImage = PLATFORM_OG_IMAGE,
  video,
}: PageMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    const canonicalUrl = `${PLATFORM_SITE_URL}${canonicalPath}`;

    const descriptionMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const previousDescription =
      descriptionMeta?.getAttribute("content") ?? PLATFORM_META_DESCRIPTION;

    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousRobots = robotsMeta?.getAttribute("content") ?? null;
    const robotsTouched = noindex || forceIndex;

    const ogTitleMeta = document.querySelector<HTMLMetaElement>(
      'meta[property="og:title"]'
    );
    const previousOgTitle =
      ogTitleMeta?.getAttribute("content") ?? PLATFORM_DEFAULT_TITLE;

    const ogDescriptionMeta = document.querySelector<HTMLMetaElement>(
      'meta[property="og:description"]'
    );
    const previousOgDescription =
      ogDescriptionMeta?.getAttribute("content") ?? PLATFORM_META_DESCRIPTION;

    const ogUrlMeta = document.querySelector<HTMLMetaElement>(
      'meta[property="og:url"]'
    );
    const previousOgUrl = ogUrlMeta?.getAttribute("content") ?? DEFAULT_CANONICAL;

    const ogImageMeta = document.querySelector<HTMLMetaElement>(
      'meta[property="og:image"]'
    );
    const previousOgImage =
      ogImageMeta?.getAttribute("content") ?? PLATFORM_OG_IMAGE;

    const twitterTitleMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="twitter:title"]'
    );
    const previousTwitterTitle =
      twitterTitleMeta?.getAttribute("content") ?? PLATFORM_DEFAULT_TITLE;

    const twitterDescriptionMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="twitter:description"]'
    );
    const previousTwitterDescription =
      twitterDescriptionMeta?.getAttribute("content") ?? PLATFORM_META_DESCRIPTION;

    const twitterImageMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="twitter:image"]'
    );
    const previousTwitterImage =
      twitterImageMeta?.getAttribute("content") ?? PLATFORM_OG_IMAGE;

    const canonicalLink = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const previousCanonical = canonicalLink?.getAttribute("href") ?? null;

    document.title = title;
    setMetaContent("name", "description", description);
    if (noindex) {
      setMetaContent("name", "robots", "noindex, nofollow");
    } else if (forceIndex) {
      setMetaContent(
        "name",
        "robots",
        "index, follow, max-image-preview:large",
      );
    }
    setMetaContent("property", "og:title", title);
    setMetaContent("property", "og:description", description);
    setMetaContent("property", "og:url", canonicalUrl);
    setMetaContent("property", "og:image", ogImage);
    setMetaContent("name", "twitter:title", title);
    setMetaContent("name", "twitter:description", description);
    setMetaContent("name", "twitter:image", ogImage);
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonicalUrl);
    }

    let videoScript: HTMLScriptElement | null = null;
    if (video) {
      videoScript = upsertJsonLdScript("jigsawml-page-video-ld", {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: video.name,
        description: video.description,
        contentUrl: video.contentUrl,
        uploadDate: video.uploadDate,
        duration: video.duration,
        thumbnailUrl: video.thumbnailUrl ?? PLATFORM_OG_IMAGE,
        embedUrl: canonicalUrl,
      });
    }

    return () => {
      document.title = previousTitle;

      if (descriptionMeta) {
        descriptionMeta.setAttribute("content", previousDescription);
      }

      if (robotsTouched && robotsMeta && previousRobots !== null) {
        robotsMeta.setAttribute("content", previousRobots);
      }

      if (ogTitleMeta) {
        ogTitleMeta.setAttribute("content", previousOgTitle);
      }

      if (ogDescriptionMeta) {
        ogDescriptionMeta.setAttribute("content", previousOgDescription);
      }

      if (ogUrlMeta) {
        ogUrlMeta.setAttribute("content", previousOgUrl);
      }

      if (ogImageMeta) {
        ogImageMeta.setAttribute("content", previousOgImage);
      }

      if (twitterTitleMeta) {
        twitterTitleMeta.setAttribute("content", previousTwitterTitle);
      }

      if (twitterDescriptionMeta) {
        twitterDescriptionMeta.setAttribute("content", previousTwitterDescription);
      }

      if (twitterImageMeta) {
        twitterImageMeta.setAttribute("content", previousTwitterImage);
      }

      if (canonicalLink && previousCanonical !== null) {
        canonicalLink.setAttribute("href", previousCanonical);
      }

      if (videoScript) {
        videoScript.remove();
      }
    };
  }, [title, description, canonicalPath, noindex, forceIndex, ogImage, video]);
}
